import React from "react";
import { TopBar } from "@/components/TopBar";
import { ROLES, ROLE_LABELS } from "@/lib/roles";
import {
  apiListPos,
  apiCreatePos,
  apiUpdatePos,
  formatApiErrorDetail,
} from "@/lib/api";
import { Loader2, Plus, Save, X, RefreshCw, Power } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { code: "", nama: "", role: "desain", deskripsi: "", urutan: 0, is_active: true };

export default function PosProduksiManagement() {
  const { user } = useAuth();
  const lihatSaja = user?.role === "manager";  // owner 2026-10-12: the Manager views, the owner changes
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY);
  const [editingId, setEditingId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await apiListPos());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); };
  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (editingId) {
        const { code, ...rest } = form; // code immutable on update
        await apiUpdatePos(editingId, { ...rest, urutan: parseInt(form.urutan, 10) || 0 });
        flash("Pos produksi diperbarui");
      } else {
        await apiCreatePos({ ...form, urutan: parseInt(form.urutan, 10) || 0 });
        flash("Pos produksi ditambahkan");
      }
      resetForm();
      await load();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ code: p.code, nama: p.nama, role: p.role || "desain", deskripsi: p.deskripsi || "", urutan: p.urutan ?? 0, is_active: p.is_active });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleActive = async (p) => {
    setError(null);
    try { await apiUpdatePos(p.id, { is_active: !p.is_active }); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60 disabled:opacity-50";
  const labelCls = "text-[13px] font-semibold text-slate-200";

  return (
    <div data-testid="pos-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Manajemen Pos Produksi</h1>
          <p className="text-sm text-slate-400 mt-1">Master data pos/stasiun produksi yang menjadi referensi workflow.</p>
        </div>

        {notice && <div data-testid="pos-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {error && <div data-testid="pos-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {!lihatSaja && <form data-testid="pos-form" onSubmit={onSubmit} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Kode {editingId && "(tidak dapat diubah)"}</label>
            <input data-testid="pos-code" value={form.code} disabled={!!editingId} onChange={(e) => setForm({ ...form, code: e.target.value })} required className={inputCls} placeholder="OP_CUTTING" />
          </div>
          <div>
            <label className={labelCls}>Nama</label>
            <input data-testid="pos-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Role/Operator</label>
            <select data-testid="pos-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
              {ROLES.map((r) => (<option key={r} value={r}>{ROLE_LABELS[r]}</option>))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Deskripsi (opsional)</label>
            <input data-testid="pos-deskripsi" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Urutan</label>
            <input data-testid="pos-urutan" type="number" value={form.urutan} onChange={(e) => setForm({ ...form, urutan: e.target.value })} className={inputCls} />
          </div>
          <div className="flex items-center gap-2 lg:col-span-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-300">
              <input data-testid="pos-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Aktif
            </label>
            <div className="ml-auto flex items-center gap-2">
              {editingId && <button type="button" data-testid="pos-cancel" onClick={resetForm} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:text-slate-100"><X size={15} /> Batal</button>}
              <button data-testid="pos-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={16} /> : editingId ? <Save size={16} /> : <Plus size={16} />}
                {editingId ? "Simpan" : "Tambah"}
              </button>
            </div>
          </div>
        </form>}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <h2 className="text-[15px] font-bold text-soq-biru-tua">Daftar Pos Produksi</h2>
            <button data-testid="pos-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pos-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-4 py-3">Urut</th>
                    <th className="px-4 py-3">Kode</th>
                    <th className="px-4 py-3">Nama</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id} data-testid={`pos-row-${p.code}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-3 font-mono text-slate-500">{p.urutan}</td>
                      <td className="px-4 py-3 font-mono text-amber-300">{p.code}</td>
                      <td className="px-4 py-3 text-slate-200">{p.nama}</td>
                      <td className="px-4 py-3 text-slate-400">{ROLE_LABELS[p.role] || p.role || "—"}</td>
                      <td className="px-4 py-3">
                        <span data-testid={`pos-status-${p.code}`} className={`text-xs font-mono px-2 py-1 rounded-md border ${p.is_active ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-600 bg-slate-700/30 text-slate-400"}`}>{p.is_active ? "Aktif" : "Nonaktif"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <a data-testid={`pos-tv-${p.code}`} href={`/tv?pos=${encodeURIComponent(p.code)}`} target="_blank" rel="noopener noreferrer" className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300">TV</a>
                          {!lihatSaja && <button data-testid={`pos-edit-${p.code}`} onClick={() => startEdit(p)} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Edit</button>}
                          {!lihatSaja && <button data-testid={`pos-toggle-${p.code}`} onClick={() => toggleActive(p)} className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-red-500/40 hover:text-red-300"><Power size={13} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Belum ada pos produksi.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
